import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import moment from 'moment-timezone'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import { getTimezoneOffsetMinutes } from '../../utils/timezone'
import ScheduleDatePicker from './ScheduleDatePicker'
import ScheduleTimeSlots from './ScheduleTimeSlots'
import classnames from 'classnames'
import useSchedulingRestrictions from '../../hooks/useSchedulingRestrictions'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import { useDateSlotConversion } from './hooks/useDateSlotConversion'
import { useScheduleSubmission } from './hooks/useScheduleSubmission'
import SchedulingHeader from './components/SchedulingHeader'
import SchedulingMetaPanel from './components/SchedulingMetaPanel'

const ConditionalRemainingSessionsSchedulingCalendar = ({
  coachDetails = null,
  onScheduleSuccess = null,
  onBack = null,
  timezone,
}) => {
  const Error = useErrorService()
  const Notification = useNotificationService()

  // Extract coach information
  const coachId = coachDetails?.coachId
  const scheduleConfig = coachDetails?.scheduleConfig
  const firstSlotDuration = scheduleConfig?.firstSlotDuration
  const otherSlotsDuration = scheduleConfig?.otherSlotsDuration
  const hasGrowthInRemaining = scheduleConfig?.hasGrowthInRemaining
  const sowSessionDuration =
    otherSlotsDuration || coachDetails?.allSessionDuration
  const sowId = coachDetails?.sowId
  const sowType = coachDetails?.sowType || coachDetails?.sow?.sowType

  const coachClientName = useMemo(() => {
    const client =
      coachDetails?.clientName || coachDetails?.client?.fullName || '-'
    const coach = coachDetails?.coachName || '-'
    return `${coach} & ${client}`
  }, [coachDetails])

  const getSessionLabel = useCallback(
    (sessionIndex) => {
      const idx = Number(sessionIndex)
      if (sowType === 'QUEST') {
        if (idx === 1) {
          return 'Preparation Session'
        }
        if (idx === 2) {
          return 'Integration Session'
        }
        if (idx === 3) {
          return 'Experience session'
        }
      }
      return `Session ${sessionIndex}`
    },
    [sowType],
  )
  const getSessionIndices = useMemo(() => {
    if (Array.isArray(coachDetails?.toBeScheduled)) {
      const indices = coachDetails.toBeScheduled
        .map((session) => session?.sessionIndex)
        .filter((index) => index !== undefined && index !== null)
        .sort((a, b) => a - b)
      if (indices.length > 0) {
        return indices
      }
    }

    if (
      coachDetails?.sessionIndex !== undefined &&
      coachDetails?.sessionIndex !== null
    ) {
      return [coachDetails.sessionIndex]
    }

    if (Array.isArray(coachDetails?.remainingSessions)) {
      const indices = coachDetails.remainingSessions
        .map((session) => session?.sessionIndex)
        .filter((index) => index !== undefined && index !== null)
        .sort((a, b) => a - b)
      if (indices.length > 0) {
        return indices
      }
    }

    if (Array.isArray(coachDetails?.schedulingConditions)) {
      const indices = coachDetails.schedulingConditions
        .map((condition) => condition?.sessionIndex)
        .filter((index) => index !== undefined && index !== null)
        .sort((a, b) => a - b)
      if (indices.length > 0) {
        return indices
      }
    }

    const remainingCount = coachDetails?.remainingSessions
    const totalSessions = coachDetails?.totalSessions
    if (
      typeof remainingCount === 'number' &&
      remainingCount > 0 &&
      typeof totalSessions === 'number' &&
      totalSessions >= remainingCount
    ) {
      const startIndex = totalSessions - remainingCount + 1
      return Array.from({ length: remainingCount }, (_, i) => startIndex + i)
    }

    if (typeof remainingCount === 'number' && remainingCount > 0) {
      return Array.from({ length: remainingCount }, (_, i) => i + 1)
    }

    return [1]
  }, [coachDetails])

  const sessionIndices = getSessionIndices

  const schedulingConditions = coachDetails?.schedulingConditions || []

  const getDateFromCondition = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return null
      }

      if (condition.primaryDate) {
        const utcMoment = moment.utc(condition.primaryDate)
        if (timezone && utcMoment.isValid()) {
          return utcMoment.tz(timezone)
        }
        return utcMoment
      }

      return null
    },
    [schedulingConditions, timezone],
  )

  const getInitialMonthForSession = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (condition?.condition === 'on_or_before') {
        return moment()
          .tz(timezone || 'UTC')
          .startOf('month')
      }

      const conditionDate = getDateFromCondition(sessionIndex)
      if (conditionDate && conditionDate.isValid()) {
        const conditionMonth = conditionDate.clone().startOf('month')
        // If condition is 'on_or_after' and primaryDate month has already passed, use current month
        if (condition?.condition === 'on_or_after') {
          const currentMonthStart = moment()
            .tz(timezone || 'UTC')
            .startOf('month')
          if (conditionMonth.isBefore(currentMonthStart, 'month')) {
            return currentMonthStart
          }
        }
        return conditionMonth
      }
      return moment()
        .tz(timezone || 'UTC')
        .startOf('month')
    },
    [getDateFromCondition, schedulingConditions, timezone],
  )

  const getInitialDateForSession = useCallback(
    (sessionIndex) => {
      const condition = schedulingConditions.find(
        (cond) => cond.sessionIndex === sessionIndex,
      )

      if (!condition) {
        return moment()
          .tz(timezone || 'UTC')
          .format('YYYY-MM-DD')
      }

      if (condition.primaryDate) {
        const primaryMoment = moment.utc(condition.primaryDate)
        if (primaryMoment.isValid()) {
          if (timezone) {
            return primaryMoment.tz(timezone).format('YYYY-MM-DD')
          }
          return primaryMoment.format('YYYY-MM-DD')
        }
      }

      return moment()
        .tz(timezone || 'UTC')
        .format('YYYY-MM-DD')
    },
    [schedulingConditions, timezone],
  )

  const [currentSessionIndex, setCurrentSessionIndex] = useState(
    sessionIndices[0] || 1,
  )
  const [sessionSlots, setSessionSlots] = useState({})
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeSlots, setTimeSlots] = useState([])
  const [availableDays, setAvailableDays] = useState({})
  const [currentMonth, setCurrentMonth] = useState(() =>
    getInitialMonthForSession(sessionIndices[0] || 1),
  )
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const preferredDateRef = useRef(selectedDate)
  const [timeFormat, setTimeFormat] = useState('12h')
  const lastLoadedSessionMonthRef = useRef(null)
  const lastLoadedSessionIndexRef = useRef(null)

  const timezoneOffsetMinutes = useMemo(() => {
    if (!timezone) {
      return null
    }
    return getTimezoneOffsetMinutes(timezone)
  }, [timezone])

  const { convertDateAndSlotToUTC } = useDateSlotConversion(timezone)

  const {
    weeklyModal,
    isConfirmingWeekly,
    closeWeeklyModal,
    handleWeeklyConfirm,
    runScheduling,
  } = useSchedulingRestrictions({
    onSuccess: (data) => {
      if (onScheduleSuccess) {
        onScheduleSuccess(data?.scheduledSessions)
      }
    },
    onSameDayConflict: (message) => {
      Notification.showNotification(message, 'danger')
    },
    onError: (message) => {
      Notification.showNotification(message, 'danger')
    },
  })

  const { handleSchedule, isScheduling } = useScheduleSubmission(
    'schedule-all',
    sowId,
    sowSessionDuration,
    timezone,
    convertDateAndSlotToUTC,
    onScheduleSuccess,
    runScheduling,
  )

  const loadMonthSlots = useCallback(
    async (targetMonth, preferredDate, sessionIndex) => {
      if (!coachId || !targetMonth || !sessionIndex) {
        return
      }

      try {
        setIsLoadingSlots(true)
        const normalizedMonth = moment(targetMonth).startOf('month')

        if (!normalizedMonth.isValid()) {
          setIsLoadingSlots(false)
          return
        }

        const monthNumber = normalizedMonth.month() + 1
        const yearNumber = normalizedMonth.year()

        const loadKey = `${sessionIndex}-${yearNumber}-${monthNumber}`
        const sessionChanged =
          lastLoadedSessionIndexRef.current !== sessionIndex

        if (!sessionChanged && lastLoadedSessionMonthRef.current === loadKey) {
          setIsLoadingSlots(false)
          return
        }

        const payload = {
          month: Number(monthNumber),
          year: Number(yearNumber),
          sessionIndex: Number(sessionIndex),
        }

        if (sowSessionDuration) {
          payload.sowSessionDuration = sowSessionDuration
        }
        if (sowId) {
          payload.sowId = sowId
        }
        if (timezone) {
          payload.clientTimezone = timezone
        }

        const { data } = await API.ClientAPI.session.getAvailableSlots(
          coachId,
          payload,
        )

        lastLoadedSessionMonthRef.current = loadKey
        lastLoadedSessionIndexRef.current = sessionIndex

        const daysMap = (data?.days || []).reduce((acc, day) => {
          if (day?.date) {
            acc[day.date] = day
          }
          return acc
        }, {})

        setAvailableDays(daysMap)

        const nextSelectableDate = (() => {
          const savedSlot = sessionSlots[sessionIndex]
          if (savedSlot?.date && daysMap[savedSlot.date]?.slots?.length) {
            return savedSlot.date
          }

          if (preferredDate && daysMap[preferredDate]?.slots?.length) {
            return preferredDate
          }

          if (!selectedDate) {
            const firstDayWithSlots = Object.values(daysMap).find(
              (day) => day?.slots?.length,
            )
            return firstDayWithSlots?.date || null
          }

          const firstDayWithSlots = Object.values(daysMap).find(
            (day) => day?.slots?.length,
          )

          return firstDayWithSlots?.date || null
        })()

        setSelectedDate(nextSelectableDate)
        setSelectedSlot(null)
      } catch (error) {
        Error.showError(error)
        setAvailableDays({})
        setSelectedDate(null)
        setSelectedSlot(null)
      } finally {
        setIsLoadingSlots(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      coachId,
      sowSessionDuration,
      timezone,
      sowId,
      schedulingConditions,
      sessionSlots,
    ],
  )

  useEffect(() => {
    preferredDateRef.current = selectedDate
  }, [selectedDate])

  useEffect(() => {
    if (!coachId || !currentMonth || currentSessionIndex === null) {
      return
    }

    // Only load if we haven't already loaded this combination
    const monthNumber = currentMonth.month() + 1
    const yearNumber = currentMonth.year()
    const loadKey = `${currentSessionIndex}-${yearNumber}-${monthNumber}`

    // Always load when session changes (even if same month was loaded before)
    const sessionChanged =
      lastLoadedSessionIndexRef.current !== currentSessionIndex
    if (sessionChanged || lastLoadedSessionMonthRef.current !== loadKey) {
      loadMonthSlots(
        currentMonth,
        preferredDateRef.current,
        currentSessionIndex,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId, currentMonth, loadMonthSlots, currentSessionIndex])

  useEffect(() => {
    if (
      currentSessionIndex !== null &&
      (!selectedDate || !availableDays[selectedDate]?.slots?.length) &&
      Object.keys(availableDays).length > 0 &&
      currentMonth
    ) {
      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot?.date && availableDays[savedSlot.date]?.slots?.length) {
        setSelectedDate(savedSlot.date)
        return
      }

      const firstDayWithSlots = Object.values(availableDays).find((day) => {
        if (!day?.slots?.length || !day?.date) return false
        const dateMoment = moment(day.date)
        return (
          dateMoment.month() === currentMonth.month() &&
          dateMoment.year() === currentMonth.year()
        )
      })
      if (firstDayWithSlots?.date) {
        setSelectedDate(firstDayWithSlots.date)
        return
      }
    }

    if (selectedDate && currentMonth) {
      const selectedDateMoment = moment(selectedDate)
      const isInCurrentMonth =
        selectedDateMoment.month() === currentMonth.month() &&
        selectedDateMoment.year() === currentMonth.year()

      if (!isInCurrentMonth) {
        setTimeSlots([])
        setSelectedSlot(null)
        return
      }
    }

    if (selectedDate && availableDays[selectedDate]?.slots?.length) {
      const slots = availableDays[selectedDate].slots
      setTimeSlots(slots)

      const savedSlot = sessionSlots[currentSessionIndex]
      if (savedSlot && savedSlot.date === selectedDate && savedSlot.slotStart) {
        let matchingSlot = slots.find(
          (slot) => slot.start === savedSlot.slotStart,
        )

        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotStartISO = moment.utc(slot.start).toISOString()
              const savedStartISO = moment
                .utc(savedSlot.slotStart)
                .toISOString()
              return slotStartISO === savedStartISO
            } catch (e) {
              return false
            }
          })
        }

        if (!matchingSlot) {
          matchingSlot = slots.find((slot) => {
            try {
              const slotMoment = moment.utc(slot.start)
              const savedMoment = moment.utc(savedSlot.slotStart)
              return slotMoment.isSame(savedMoment)
            } catch (e) {
              return false
            }
          })
        }

        if (matchingSlot) {
          setSelectedSlot(matchingSlot)
        } else {
          setSelectedSlot(null)
        }
      } else if (!savedSlot || savedSlot.date !== selectedDate) {
        setSelectedSlot(null)
      }
      return
    }

    setTimeSlots([])
    if (selectedDate && !availableDays[selectedDate]?.slots?.length) {
      setSelectedSlot(null)
    }
  }, [
    selectedDate,
    availableDays,
    currentSessionIndex,
    sessionSlots,
    currentMonth,
  ])

  useEffect(() => {
    const savedSlot = sessionSlots[currentSessionIndex]
    const sessionChanged =
      lastLoadedSessionIndexRef.current !== currentSessionIndex

    if (savedSlot) {
      if (selectedDate !== savedSlot.date) {
        setSelectedDate(savedSlot.date)
        const dateMoment = moment(savedSlot.date)
        if (dateMoment.isValid()) {
          const monthStart = dateMoment.clone().startOf('month')
          setCurrentMonth((prev) => {
            if (prev && prev.isSame(monthStart, 'month')) {
              return prev
            }
            lastLoadedSessionMonthRef.current = null
            return monthStart
          })
          preferredDateRef.current = savedSlot.date
        }
      } else {
        const monthNumber = currentMonth.month() + 1
        const yearNumber = currentMonth.year()
        const loadKey = `${currentSessionIndex}-${yearNumber}-${monthNumber}`

        if (sessionChanged && lastLoadedSessionMonthRef.current !== loadKey) {
          lastLoadedSessionMonthRef.current = null
        }
      }
    } else {
      const initialDate = getInitialDateForSession(currentSessionIndex)
      const initialMonth = getInitialMonthForSession(currentSessionIndex)

      if (selectedDate !== initialDate) {
        setSelectedDate(initialDate)
        setCurrentMonth((prev) => {
          if (prev && prev.isSame(initialMonth, 'month')) {
            return prev
          }
          lastLoadedSessionMonthRef.current = null
          return initialMonth
        })
        preferredDateRef.current = initialDate
        setSelectedSlot(null)
      } else {
        const monthNumber = currentMonth.month() + 1
        const yearNumber = currentMonth.year()
        const loadKey = `${currentSessionIndex}-${yearNumber}-${monthNumber}`

        if (sessionChanged && lastLoadedSessionMonthRef.current !== loadKey) {
          lastLoadedSessionMonthRef.current = null
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionIndex])

  const handleDateSelect = (date) => {
    setSelectedDate(date)
    setSelectedSlot(null)
  }

  const handleMonthChange = useCallback((month) => {
    if (!month) {
      return
    }

    const normalizedMonth = moment(month).startOf('month')
    setCurrentMonth((prev) => {
      if (prev && prev.isSame(normalizedMonth, 'month')) {
        return prev
      }

      return normalizedMonth
    })
  }, [])

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot)

    if (selectedDate && slot) {
      const durationMinutes = Number(sowSessionDuration) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(selectedDate, slot.start, slot.end)

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }
  }

  const canAccessSession = useCallback(
    (sessionIndex) => {
      if (sessionIndex === sessionIndices[0]) {
        return true
      }

      const currentIndex = sessionIndices.indexOf(sessionIndex)
      if (currentIndex <= 0) {
        return true
      }

      const previousSessionIndex = sessionIndices[currentIndex - 1]
      const previousSessionSlot = sessionSlots[previousSessionIndex]

      if (currentIndex > sessionIndices.indexOf(currentSessionIndex)) {
        return previousSessionSlot?.slotStart !== undefined
      }

      return true
    },
    [sessionIndices, sessionSlots, currentSessionIndex],
  )

  const handleSessionClick = (sessionIndex) => {
    if (sessionIndex === currentSessionIndex) {
      return
    }

    // Check if session can be accessed
    if (!canAccessSession(sessionIndex)) {
      const currentIndex = sessionIndices.indexOf(sessionIndex)
      const previousSessionIndex = sessionIndices[currentIndex - 1]
      Error.showError({
        message: `Please book ${getSessionLabel(
          previousSessionIndex,
        )} first before booking ${getSessionLabel(sessionIndex)}.`,
      })
      return
    }

    if (selectedDate && selectedSlot) {
      const durationMinutes = Number(sowSessionDuration) || 0
      const { start: slotStart, end: slotEndFromConversion } =
        convertDateAndSlotToUTC(
          selectedDate,
          selectedSlot.start,
          selectedSlot.end,
        )

      const slotEnd = durationMinutes
        ? slotStart.clone().add(durationMinutes, 'minutes')
        : slotEndFromConversion || slotStart.clone()

      setSessionSlots((prev) => ({
        ...prev,
        [currentSessionIndex]: {
          date: selectedDate,
          slotStart: slotStart.toISOString(),
          slotEnd: slotEnd.toISOString(),
        },
      }))
    }

    lastLoadedSessionMonthRef.current = null
    lastLoadedSessionIndexRef.current = null
    setCurrentSessionIndex(sessionIndex)
  }

  const handleNext = () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    // Save current session's selection
    const durationMinutes = Number(sowSessionDuration) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
      )

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const updatedSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    setSessionSlots(updatedSlots)

    const currentIndex = sessionIndices.indexOf(currentSessionIndex)
    if (currentIndex < sessionIndices.length - 1) {
      const nextSessionIndex = sessionIndices[currentIndex + 1]
      const savedSlot = updatedSlots[nextSessionIndex]
      let targetDate = null
      let targetMonth = null

      if (savedSlot) {
        const dateMoment = moment(savedSlot.date)
        if (dateMoment.isValid()) {
          targetDate = savedSlot.date
          targetMonth = dateMoment.clone().startOf('month')
        }
      } else {
        targetDate = getInitialDateForSession(nextSessionIndex)
        targetMonth = getInitialMonthForSession(nextSessionIndex)
      }

      setCurrentSessionIndex(nextSessionIndex)
      lastLoadedSessionIndexRef.current = nextSessionIndex

      if (targetDate) {
        setSelectedDate(targetDate)
        preferredDateRef.current = targetDate
      }
      if (targetMonth) {
        const monthNumber = targetMonth.month() + 1
        const yearNumber = targetMonth.year()
        const loadKey = `${nextSessionIndex}-${yearNumber}-${monthNumber}`

        if (lastLoadedSessionMonthRef.current !== loadKey) {
          lastLoadedSessionMonthRef.current = null
        }

        setCurrentMonth(targetMonth)
      }
    }
  }

  const handleScheduleAll = useCallback(async () => {
    if (!selectedDate || !selectedSlot) {
      Error.showError({ message: 'Please select a date and time slot' })
      return
    }

    const durationMinutes = Number(sowSessionDuration) || 0
    const { start: slotStart, end: slotEndFromConversion } =
      convertDateAndSlotToUTC(
        selectedDate,
        selectedSlot.start,
        selectedSlot.end,
      )

    const slotEnd = durationMinutes
      ? slotStart.clone().add(durationMinutes, 'minutes')
      : slotEndFromConversion || slotStart.clone()

    const finalSlots = {
      ...sessionSlots,
      [currentSessionIndex]: {
        date: selectedDate,
        slotStart: slotStart.toISOString(),
        slotEnd: slotEnd.toISOString(),
      },
    }

    const allSessionsHaveSlots = sessionIndices.every(
      (sessionIndex) => finalSlots[sessionIndex]?.slotStart,
    )

    if (!allSessionsHaveSlots) {
      Error.showError({
        message: 'Please select slots for all sessions before scheduling.',
      })
      return
    }

    try {
      const selectedDatesList = sessionIndices.map((sessionIndex) => {
        const slotData = finalSlots[sessionIndex]
        return {
          date: slotData.date,
          slotStart: slotData.slotStart,
          slotEnd: slotData.slotEnd,
          isSlotAvailable: true,
          sessionIndex: Number(sessionIndex),
        }
      })

      const result = await handleSchedule(null, null, selectedDatesList)
      if (result?.success) {
        Notification.showNotification(result.message, 'success')
      }
    } catch (error) {
      if (error.message) {
        Notification.showNotification(error.message, 'danger')
      } else {
        Error.showError(error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    selectedSlot,
    sessionSlots,
    currentSessionIndex,
    sessionIndices,
    sowSessionDuration,
    convertDateAndSlotToUTC,
    handleSchedule,
    Notification,
  ])

  const currentIndex = sessionIndices.indexOf(currentSessionIndex)
  const isLastSession = currentIndex === sessionIndices.length - 1
  const isSubmitting = isScheduling || isConfirmingWeekly
  const isActionDisabled = isSubmitting || !selectedDate || !selectedSlot
  const isSchedulingSingleSession = sessionIndices.length === 1
  const schedulePageTitle = isSchedulingSingleSession
    ? 'Schedule Session'
    : 'Schedule Multiple Sessions'
  const scheduleButtonText = isSchedulingSingleSession
    ? 'SCHEDULE'
    : 'Schedule Multiple Sessions'

  return (
    <div className='first-session-schedule'>
      <SchedulingHeader title={schedulePageTitle} onBack={onBack} />
      <SchedulingMetaPanel
        coachClientName={coachClientName}
        duration={sowSessionDuration}
        totalSessions={sessionIndices.length}
        firstSlotDuration={firstSlotDuration}
        otherSlotsDuration={otherSlotsDuration}
        hasGrowthInRemaining={hasGrowthInRemaining}
        sowType={sowType}
      />
      <div className='first-session-schedule mt-30px'>
        <div
          className='first-session-schedule__conditional-planner border border-dark border-1 rounded d-flex overflow-hidden'
          style={{ minHeight: 490, height: 490 }}>
          <div
            className='flex-shrink-0 p-3 border border-dark border-top-0 border-bottom-0 border-left-0 d-flex flex-column overflow-hidden'
            style={{ minWidth: 300, width: 300, minHeight: 0 }}>
            <div
              className='d-flex flex-column overflow-auto'
              style={{ minHeight: 0 }}>
              {sessionIndices.map((sessionIndex) => {
                const isActive = sessionIndex === currentSessionIndex
                const hasSlot = sessionSlots[sessionIndex]?.slotStart
                const isAccessible = canAccessSession(sessionIndex)
                const isDisabled = !isAccessible || isSubmitting

                return (
                  <Button
                    key={sessionIndex}
                    variant='abroad-blue'
                    size='lg'
                    className={classnames(
                      'py-2 mb-1 cursor-pointer time-slot-picker__slot w-100 text-center d-flex flex-column align-items-center',
                      {
                        'time-slot-picker__slot--selected': isActive,
                      },
                    )}
                    onClick={() => handleSessionClick(sessionIndex)}
                    disabled={isDisabled}>
                    <div className='sa5 font-weight-bold'>
                      {getSessionLabel(sessionIndex)}
                    </div>
                    {hasSlot && (
                      <div
                        className={classnames('s6 text-muted mt-2 mb-0', {
                          'text-white-50': isActive,
                          'text-muted': !isActive,
                        })}>
                        {moment(sessionSlots[sessionIndex].date).format(
                          'MMM D',
                        )}
                        {', '}
                        {timezone
                          ? moment
                              .utc(sessionSlots[sessionIndex].slotStart)
                              .tz(timezone)
                              .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
                          : timezoneOffsetMinutes !== null
                          ? moment
                              .utc(sessionSlots[sessionIndex].slotStart)
                              .utcOffset(timezoneOffsetMinutes)
                              .format(timeFormat === '12h' ? 'h:mm A' : 'HH:mm')
                          : moment(sessionSlots[sessionIndex].slotStart).format(
                              timeFormat === '12h' ? 'h:mm A' : 'HH:mm',
                            )}
                      </div>
                    )}
                  </Button>
                )
              })}
            </div>
          </div>
          <div
            className='flex-grow-1 p-3 border border-dark border-top-0 border-bottom-0 border-left-0 d-flex flex-column overflow-hidden'
            style={{ minHeight: 0 }}>
            <ScheduleDatePicker
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              minDate={moment().format('YYYY-MM-DD')}
              currentMonth={currentMonth}
              onMonthChange={handleMonthChange}
              availableDays={availableDays}
              disablePastDates={true}
              isLoading={isLoadingSlots}
              disableDatesWithoutSlots={true}
            />
          </div>
          <div
            className='flex-shrink-0 p-3 d-flex flex-column overflow-hidden'
            style={{ minWidth: 300, width: 300, minHeight: 0 }}>
            <ScheduleTimeSlots
              slots={timeSlots}
              selectedSlot={selectedSlot}
              onSlotSelect={handleSlotSelect}
              timeFormat={timeFormat}
              onTimeFormatChange={setTimeFormat}
              isLoading={isLoadingSlots}
              timezoneOffsetMinutes={timezoneOffsetMinutes}
              timezone={timezone}
            />
          </div>
        </div>
        <div className='d-flex justify-content-end mt-3'>
          <div>
            {isLastSession ? (
              <Button
                variant={isActionDisabled ? 'gray' : 'saffron'}
                size='md'
                className='line-height-normal'
                onClick={handleScheduleAll}
                disabled={isActionDisabled}>
                {isSubmitting ? 'SCHEDULING...' : scheduleButtonText}
              </Button>
            ) : (
              <Button
                variant={isActionDisabled ? 'gray' : 'saffron'}
                size='md'
                className='line-height-normal cursor-pointer'
                onClick={handleNext}
                disabled={isActionDisabled}>
                NEXT
              </Button>
            )}
          </div>
        </div>
      </div>
      <WeeklySessionWarningModal
        isOpen={weeklyModal.isOpen}
        message={weeklyModal.message}
        onConfirm={handleWeeklyConfirm}
        onCancel={closeWeeklyModal}
        loading={isConfirmingWeekly}
      />
    </div>
  )
}

export default ConditionalRemainingSessionsSchedulingCalendar
